import React, { useCallback, useEffect, useState } from 'react';
import BEM from '@upsales/components/Utils/bemClass';
import { Card, Flex, Title, Text, Icon, Loader, Progressbar } from '@upsales/components';
import { useTranslation } from 'Components/Helpers/translate';
import NotificationService from 'App/babel/NotificationService';
import logError from 'Helpers/logError';
import OutreachStepRow from 'Components/OutreachActions/OutreachStepRow';
import { useOutreachSteps } from 'Components/OutreachActions/useOutreachSteps';
import { unwrap, type ActionableStep } from 'Components/OutreachActions/types';

import './OutreachPlanCard.scss';

type ClientEngagementPlan = {
	id: number;
	status: 'generating' | 'ready' | 'failed' | 'cancelled' | 'discarded' | 'completed';
	summary: string | null;
	stepCount: number;
	scheduledCount: number;
	completedCount: number;
};

type Props = {
	clientId: number;
};

/**
 * "Engagement plan" — the Prospecting Agent's outreach for this company (WEB-8617),
 * and the reaches the rep can trigger from it (WEB-8739).
 *
 * One card rather than two: the progress and the steps it counts belong together, and
 * acting on a step has to move the bar without a reload. Collapsed it's a glanceable
 * summary; expanding it lists the approved-but-not-done reaches, each as its channel's
 * action. The header links out to the full plan review.
 *
 * Self-fetches keyed by clientId, gated server-side behind OUTREACH_AGENT, and returns
 * null when there's nothing worth showing.
 */
const OutreachPlanCard = ({ clientId }: Props) => {
	const { t } = useTranslation();
	const [plans, setPlans] = useState<ClientEngagementPlan[] | null>(null);
	const [busyId, setBusyId] = useState<number | null>(null);
	const [expandedId, setExpandedId] = useState<number | null>(null);
	const [expandedStepId, setExpandedStepId] = useState<number | null>(null);

	// Carrying out a reach closes its to-do, which is what the progress bar counts —
	// so move the plan's own numbers here instead of refetching the card (WEB-8739).
	const onStepDone = useCallback((step: ActionableStep) => {
		setPlans(prev =>
			prev
				? prev.map(plan =>
						plan.id === step.planId
							? { ...plan, completedCount: Math.min(plan.completedCount + 1, plan.stepCount) }
							: plan
				  )
				: prev
		);
	}, []);

	const { steps, busyStepId, recordDone, sendEmail } = useOutreachSteps({ clientId, onStepDone });

	// Mark a plan done from the card (WEB-9355/9136) — clears it here without opening the
	// review; keeps the plan + its activities. Drops the plan locally on success.
	const markDone = async (e: React.MouseEvent, planId: number) => {
		e.preventDefault();
		e.stopPropagation();
		if (busyId !== null) return;
		setBusyId(planId);
		const res = await fetch(`/api/v2/prospectingAgentRuns/engagementPlan/${planId}/complete`, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' }
		}).catch(() => null);
		setBusyId(null);
		if (!res || !res.ok) {
			NotificationService.add({
				icon: 'times',
				style: NotificationService.style.ERROR,
				title: 'prospectingAgent.completePlanFailed'
			});
			return;
		}
		setPlans(prev => (prev ? prev.filter(p => p.id !== planId) : prev));
	};

	useEffect(() => {
		// The Angular react-driver can mount this before AccountCtrl.account has
		// loaded, so clientId is briefly undefined — stay empty until it arrives (the
		// driver's `watch` re-renders us with the real id once it resolves).
		if (!clientId || clientId <= 0) {
			setPlans(null);
			return;
		}
		let cancelled = false;
		fetch(`/api/v2/prospectingAgentRuns/client/${clientId}/engagementPlans`)
			.then(r => r.json())
			.then(body => {
				if (cancelled) return;
				const rows = unwrap<ClientEngagementPlan[]>(body);
				setPlans(Array.isArray(rows) ? rows : []);
			})
			.catch(err => {
				// Don't blank the company card on a transient failure — leave the
				// section empty and surface the cause for diagnosis.
				logError(err, 'OutreachPlanCard: failed to load engagement plans');
			});
		return () => {
			cancelled = true;
		};
	}, [clientId]);

	// Ready plans with steps, plus in-flight generations, are a useful signal on the
	// account overview. Approving a plan schedules to-dos but doesn't complete the
	// outreach, so a fully-scheduled plan stays visible (WEB-8734) — it's not "done".
	// Failed / cancelled / empty plans are noise here (managed from the Prospecting
	// Agent page).
	const visible = (plans ?? []).filter(
		plan => plan.status === 'generating' || (plan.status === 'ready' && plan.stepCount > 0)
	);

	if (!visible.length) {
		return null;
	}

	const CSS = new BEM('OutreachPlanCard');

	return (
		<div className={CSS.b()}>
			{visible.map(plan => {
				const generating = plan.status === 'generating';
				// Real progress: activities actually carried out, not merely scheduled (WEB-9355).
				const pct = plan.stepCount > 0 ? Math.round((plan.completedCount / plan.stepCount) * 100) : 0;
				// Offer "done" once the rep has actioned the plan (steps scheduled); an unapproved
				// plan is handled from the review instead.
				const canMarkDone = !generating && plan.scheduledCount > 0;
				const planSteps = steps.filter(step => step.planId === plan.id);
				const expanded = expandedId === plan.id;
				const canExpand = !generating && planSteps.length > 0;

				return (
					<Card key={plan.id} className={CSS.elem('card').mod({ expandable: canExpand }).b()}>
						<div
							className={CSS.elem('header').b()}
							onClick={() => canExpand && setExpandedId(expanded ? null : plan.id)}
						>
							<Flex justifyContent="space-between" alignItems="center" gap={8}>
								<Flex alignItems="center" gap={8}>
									<Icon name="paper-plane" color="bright-blue" />
									<Title size="sm">{t('prospectingAgent.engagementPlan')}</Title>
									{canExpand ? (
										<Text size="sm" color="grey-10">
											{t('prospectingAgent.stepsReadyToReachOut', { count: planSteps.length })}
										</Text>
									) : null}
								</Flex>
								<Flex alignItems="center" gap={8}>
									{canMarkDone ? (
										<button
											type="button"
											className={CSS.elem('done').b()}
											disabled={busyId === plan.id}
											onClick={e => markDone(e, plan.id)}
										>
											{t('prospectingAgent.markPlanDone')}
										</button>
									) : null}
									{/* The full review, rather than the old chevron that looked like an
									    expander but navigated (WEB-8739). */}
									{generating ? null : (
										<a
											className={CSS.elem('open').b()}
											href={`#/prospecting-agent?plan=${plan.id}&from=plans`}
											onClick={e => e.stopPropagation()}
										>
											{t('prospectingAgent.openPlan')}
										</a>
									)}
									{generating ? (
										<Loader size="xs" />
									) : canExpand ? (
										<Icon
											name={expanded ? 'chevron-up' : 'chevron-down'}
											color="grey-8"
											className={CSS.elem('chevron').b()}
										/>
									) : null}
								</Flex>
							</Flex>

							{generating ? (
								<Text size="sm" color="grey-11" space="mts">
									{t('prospectingAgent.engagementPlanGenerating')}
								</Text>
							) : (
								<div className={CSS.elem('progress').b()}>
									<Progressbar size="sm" value={pct} color="bright-blue" />
									<Text size="sm" bold={true} space="mts">
										{t('prospectingAgent.stepsDoneOfTotal', {
											done: plan.completedCount,
											total: plan.stepCount
										})}
									</Text>
								</div>
							)}

							{plan.summary ? (
								<Text
									size="sm"
									color="grey-10"
									ellipsis={true}
									space="mts"
									className={CSS.elem('summary').b()}
								>
									{plan.summary}
								</Text>
							) : null}
						</div>

						{expanded ? (
							<div className={CSS.elem('steps').b()}>
								{planSteps.map(step => (
									<OutreachStepRow
										key={step.stepId}
										step={step}
										busy={busyStepId === step.stepId}
										expanded={expandedStepId === step.stepId}
										onToggle={() =>
											setExpandedStepId(expandedStepId === step.stepId ? null : step.stepId)
										}
										onSend={() => sendEmail(step)}
										onDone={() => recordDone(step)}
									/>
								))}
							</div>
						) : null}
					</Card>
				);
			})}
		</div>
	);
};

export default OutreachPlanCard;
